import { useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';

/**
 * Hook for accessing authentication context throughout the application.
 * This is the standardized implementation of useAuth that should be used
 * in all components.
 * 
 * @returns AuthContextType plus additional utility functions
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  /**
   * Get the current authentication token from localStorage
   * @returns The current token or null if not found
   */
  const getToken = (): string | null => {
    return localStorage.getItem('token');
  };
  
  /**
   * Get authentication headers for API requests
   * @returns Object with Authorization header or empty object if no token
   */
  const getAuthHeaders = (): Record<string, string> => {
    const token = getToken();
    if (!token) {
      return {};
    }
    return {
      'Authorization': `Bearer ${token}`
    };
  };
  
  /**
   * Validate the current authentication token
   * @returns Object with validation status and details
   */
  const validateToken = () => {
    const token = getToken();
    if (!token) {
      return { valid: false, error: 'No token found' };
    }
    
    // Check token structure
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      return { valid: false, error: 'Invalid token structure' };
    }
    
    try {
      // Decode the payload
      const payload = JSON.parse(atob(tokenParts[1]));
      
      // Check expiration
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp < now) {
        return { valid: false, error: 'Token expired', expiredAt: new Date(payload.exp * 1000) };
      }
      
      return { 
        valid: true, 
        payload, 
        expiresAt: payload.exp ? new Date(payload.exp * 1000) : null 
      };
    } catch (error) {
      return { valid: false, error: 'Failed to decode token' };
    }
  };
  
  /**
   * Reset authentication by removing token and logging out
   * @returns true on successful reset
   */
  const resetAuthToken = () => {
    localStorage.removeItem('token');
    context.logout();
    return true;
  };
  
  // Return context with additional helper functions
  return {
    ...context,
    getToken,
    getAuthHeaders,
    validateToken,
    resetAuthToken
  };
}; 